"use client"

/**
 * Actor v3 管理用React Hooks
 * プロセス代数とLangChain.js互換性対応
 * https://actor.gftd.ai/api/v3/actor
 */

import { useState, useEffect, useCallback } from "react"
import {
  ActorV3Client,
  ActorV3Runnable,
  actorV3Api,
  createSampleBehavior
} from "@/lib/api/actor-v3-client"
import {
  ActorID,
  Message,
  State,
  Behavior,
  CreateActorRequest,
  CreateActorResponse,
  SendMessageRequest,
  ReceiveMessagesRequest,
  ListActorsResponse,
  ActorSystemConfig,
  SystemStatsResponse,
  ApiV3Response,
  createMessage
} from "@/lib/types/actor-v3"

/**
 * Actor v3 一覧管理Hook
 */
export function useActorV3List(organizationId?: string) {
  const [actors, setActors] = useState<ListActorsResponse["actors"]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)

  const fetchActors = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('🎭 Fetching v3 actors with process algebra support:', organizationId)
      const response = await actorV3Api.actors.list(organizationId)
      
      if (response.success && response.data) {
        // 安全なデータ取得
        const actors = response.data.actors || []
        const totalCount = response.data.total_count || 0
        
        setActors(actors)
        setTotalCount(totalCount)
        
        console.log('✅ v3 actors loaded:', {
          count: actors.length,
          total: totalCount,
          actors: actors.map(a => ({
            id: a?.actor_id || 'unknown',
            name: a?.behavior_name || 'unnamed',
            status: a?.status || 'unknown'
          }))
        })
      } else {
        setError(response.error?.message || "Failed to fetch v3 actors")
        console.error('❌ v3 actors fetch failed:', response.error)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      setError(errorMsg)
      console.warn('❌ v3 actors fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  // APIが利用可能になったので自動実行を有効化
  useEffect(() => {
    fetchActors()
  }, [fetchActors])

  const refetch = useCallback(() => {
    fetchActors()
  }, [fetchActors])

  return {
    actors,
    loading,
    error,
    totalCount,
    refetch,
  }
}

/**
 * Actor v3 作成Hook
 */
export function useActorV3Creation() {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const createActor = useCallback(async (
    behavior: Behavior,
    organizationId: string,
    initialState?: State
  ): Promise<CreateActorResponse | null> => {
    setLoading(true)
    setError(null)

    try {
      console.log('🎭 Creating v3 actor with process algebra behavior:', {
        name: behavior.name,
        version: behavior.version,
        processAlgebra: behavior.process_algebra,
        capabilities: behavior.metadata.capabilities
      })

      const request: CreateActorRequest = {
        behavior,
        organization_id: organizationId,
        initial_state: initialState
      }

      const response = await actorV3Api.actors.create(request)
      
      if (response.success && response.data) {
        console.log('✅ v3 actor created:', {
          actor_id: response.data.actor_id,
          status: response.data.status,
          timestamp: response.data.timestamp
        })
        return response.data
      } else {
        setError(response.error?.message || "Failed to create v3 actor")
        console.error('❌ v3 actor creation failed:', response.error)
        return null
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      setError(errorMsg)
      console.error('❌ v3 actor creation error:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const createSampleActor = useCallback(async (
    name: string,
    description: string,
    organizationId: string
  ) => {
    const behavior = createSampleBehavior(name, description)
    return createActor(behavior, organizationId)
  }, [createActor])

  return {
    createActor,
    createSampleActor,
    loading,
    error,
  }
}

/**
 * Actor v3 メッセージング Hook
 */
export function useActorV3Messaging(actorId: ActorID) {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])

  const sendMessage = useCallback(async (
    label: string,
    payload: Record<string, any>,
    target?: ActorID
  ) => {
    setLoading(true)
    setError(null)

    try {
      const message = createMessage(label, payload)
      const request: SendMessageRequest = {
        target: target || actorId,
        message
      }

      console.log('📨 Sending v3 message:', {
        actorId,
        label,
        payload,
        target: target || actorId
      })

      const response = await actorV3Api.actors.send(actorId, request)
      
      if (response.success) {
        console.log('✅ v3 message sent:', response.data)
        return response.data
      } else {
        setError(response.error?.message || "Failed to send message")
        console.error('❌ v3 message send failed:', response.error)
        return null
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      setError(errorMsg)
      console.error('❌ v3 message send error:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [actorId])

  const receiveMessages = useCallback(async (
    request: ReceiveMessagesRequest = {}
  ) => {
    setLoading(true)
    setError(null)

    try {
      console.log('📬 Receiving v3 messages:', { actorId, request })
      
      const response = await actorV3Api.actors.receive(actorId, request)
      
      if (response.success && response.data) {
        const newMessages = response.data.messages
        setMessages(prev => [...prev, ...newMessages])
        
        console.log('✅ v3 messages received:', {
          count: newMessages.length,
          behavior_results: response.data.behavior_results.length
        })
        
        return response.data
      } else {
        setError(response.error?.message || "Failed to receive messages")
        console.error('❌ v3 message receive failed:', response.error)
        return null
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      setError(errorMsg)
      console.error('❌ v3 message receive error:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [actorId])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  return {
    sendMessage,
    receiveMessages,
    clearMessages,
    messages,
    loading,
    error,
  }
}

/**
 * LangChain.js互換 Actor v3 Hook
 */
export function useActorV3Runnable(actorId: ActorID) {
  const [runnable] = useState(() => new ActorV3Runnable(actorId, actorV3Api))
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const invoke = useCallback(async (input: any, config?: any) => {
    setLoading(true)
    setError(null)

    try {
      console.log('🔄 v3 LangChain invoke:', { actorId, input })
      const result = await runnable.invoke(input, config)
      console.log('✅ v3 LangChain invoke result:', result)
      return result
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Invoke failed"
      setError(errorMsg)
      console.error('❌ v3 LangChain invoke error:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [runnable, actorId])

  const batch = useCallback(async (inputs: any[], config?: any) => {
    setLoading(true)
    setError(null)

    try {
      console.log('🔄 v3 LangChain batch:', { actorId, inputs })
      const results = await runnable.batch(inputs, config)
      console.log('✅ v3 LangChain batch results:', results)
      return results
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Batch failed"
      setError(errorMsg)
      console.error('❌ v3 LangChain batch error:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [runnable, actorId])

  return {
    runnable,
    invoke,
    batch,
    loading,
    error,
  }
}

/**
 * Actor v3 システム管理Hook
 */
export function useActorV3System() {
  const [config, setConfig] = useState<ActorSystemConfig | null>(null)
  const [stats, setStats] = useState<SystemStatsResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('⚙️ Fetching v3 system config with process algebra analysis')
      const response = await actorV3Api.system.getConfig()
      
      if (response.success && response.data) {
        setConfig(response.data)
        console.log('✅ v3 system config loaded:', {
          deadlock_free: response.data.process_algebra_analysis.deadlock_free,
          livelock_free: response.data.process_algebra_analysis.livelock_free,
          topology_edges: response.data.communication_topology.edges.length
        })
      } else {
        const errorMsg = response.error?.message || "Failed to fetch system config"
        setError(errorMsg)
        console.warn('⚠️ v3 system config fetch failed:', response.error)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      setError(errorMsg)
      console.warn('❌ v3 system config fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('📊 Fetching v3 system stats')
      const response = await actorV3Api.system.getStats()
      
      if (response.success && response.data) {
        setStats(response.data)
        console.log('✅ v3 system stats loaded:', {
          active_actors: response.data.stats.active_actors,
          total_messages: response.data.stats.total_messages,
          throughput: response.data.detailed_metrics.performance_metrics.throughput
        })
      } else {
        const errorMsg = response.error?.message || "Failed to fetch system stats"
        setError(errorMsg)
        console.warn('⚠️ v3 system stats fetch failed:', response.error)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      setError(errorMsg)
      console.warn('❌ v3 system stats fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchConfig(), fetchStats()])
  }, [fetchConfig, fetchStats])

  // 自動実行は無効化 - 必要に応じて手動でrefreshAllを呼び出す
  // useEffect(() => {
  //   refreshAll()
  // }, [refreshAll])

  return {
    config,
    stats,
    loading,
    error,
    fetchConfig,
    fetchStats,
    refreshAll,
  }
}

/**
 * Actor v3 ヘルスチェック Hook
 */
export function useActorV3Health() {
  const [status, setStatus] = useState<'checking' | 'healthy' | 'unhealthy' | 'unknown'>('unknown')
  const [lastChecked, setLastChecked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const checkHealth = useCallback(async () => {
    setStatus('checking')
    setError(null)

    try {
      console.log('🏥 Checking v3 API health')
      
      // 簡単なヘルスチェック：actor一覧を取得
      const response = await actorV3Api.actors.list()
      
      if (response.success) {
        setStatus('healthy')
        setLastChecked(new Date().toISOString())
        console.log('✅ v3 API is healthy')
      } else {
        setStatus('unhealthy')
        setError(response.error?.message || "Health check failed")
        console.warn('❌ v3 API health check failed:', response.error)
      }
    } catch (err) {
      setStatus('unhealthy')
      const errorMsg = err instanceof Error ? err.message : "Health check error"
      setError(errorMsg)
      console.warn('❌ v3 API health check error:', err)
    }
  }, [])

  // APIが利用可能になったのでヘルスチェックを有効化
  useEffect(() => {
    checkHealth()
    
    // 定期的なヘルスチェック（1分間隔）
    const interval = setInterval(checkHealth, 60000)
    return () => clearInterval(interval)
  }, [checkHealth])

  return {
    status,
    lastChecked,
    error,
    checkHealth,
  }
} 